import { DOCUMENT, ElementRef, inject, Injectable, Renderer2 } from '@angular/core';
import {
  IntermediateReportInterface,
  LearnosityAssessmentApiService,
  LearnosityItemsConfigInterface,
  LearnosityResponseStatusEnum,
  LearnositySessionResponsesDataResponseInterface,
  Permissions,
} from '@campus/dal';
import { PermissionService } from '@campus/shared';
import { ObservableFunctions } from '@campus/utils';
import { first, Observable, Subject, take } from 'rxjs';
import { learnosityScriptVersion } from '../../constants/learnosity-version';
import {
  ItemsAppInterface,
  LearnosityErrorCodeInterface,
  QuestionObjectInterface,
  SubmitOptionsInterface,
} from '../../interfaces';
import { LEARNOSITY_WINDOW } from '../../services/learnosity.window';
const { on, repeatUntil } = ObservableFunctions;

export enum LearnosityEvent {
  PLAYER_READY = 'dk:learnosity:ready',
  PLAYER_ERROR = 'dk:learnosity:error',
  PLAYER_STOPPED = 'dk:learnosity:stopped',

  REPORT_INITIAL = 'dk:report:initial',
  REPORT_INTERMEDIATE = 'dk:report:intermediate',
  REPORT_FINAL = 'dk:report:final',

  TEST_START = 'dk:test:start',
  TEST_LOADED = 'dk:test:loaded',
  TEST_SAVE_SUCCESS = 'dk:test:save:success',
  TEST_SAVE_ERROR = 'dk:test:save:error',
  TEST_SUBMIT_START = 'dk:test:submit:start',
  TEST_FINISHED_SAVE = 'dk:test:finished:save',
  TEST_FINISHED_SUBMIT = 'dk:test:finished:submit',

  ITEM_LOADED = 'dk:item:loaded',
  ITEM_UNLOAD = 'dk:item:unload',
  ITEM_CHANGED = 'dk:item:changed',
  ITEM_GOTO = 'dk:item:goto',
}

const NON_VALIDATABLE_QUESTION_TYPES = new Set<string>(['essay', 'extended_text', 'audio_recording']);
const SUPPORTS_VALIDATE_CUSTOM_TYPES = new Set<string>([
  'number_tile_custom_question',
  'hundred_field_custom_question',
  'multicolor_token_hightlight_custom_question',
  'clock_custom_question',
  'connect_custom_question',
  'custom_question_zone_marker',
  'connect_on_picture_custom_question',
]);

class Shared {
  public static calculateAttemptScores(responses: LearnositySessionResponsesDataResponseInterface[]) {
    return responses.reduce((acc, { response, score, max_score }) => {
      if (!response) {
        acc.push([0]);

        return acc;
      }

      const feedbackAttemptsCount = response.feedbackAttemptsCount;
      const scorePercentage = Math.round((score / max_score) * 100);

      // TODO: how to get different attempts' scores? right now the previous attempts just count as score null
      const scores = new Array(feedbackAttemptsCount)
        .fill(0)
        .map((_, index, arr) => (index === arr.length - 1 ? scorePercentage : null));

      acc.push(scores);

      return acc;
    }, []);
  }
}

class QuestionInfo {
  constructor(
    private itemReference: string,
    private report: IntermediateReportInterface
  ) {}

  public get lastResponseIsAttempt(): boolean {
    const relevantResponses = this.responses;
    if (!relevantResponses.length) return false;

    return relevantResponses[relevantResponses.length - 1].attempted;
  }

  public get questionScores() {
    return this.toQuestionScores(this.responses);
  }

  private get responses() {
    const allResponses = this.report.data.learnosityResponses?.data[0]?.responses || [];
    const relevantResponses = allResponses.filter((response) => response.item_reference === this.itemReference);

    return relevantResponses;
  }

  private toQuestionScores(responses: LearnositySessionResponsesDataResponseInterface[]): {
    questionReference: string;
    score: number;
    attempts: number;
    attemptScores: number[];
    hintRequested: boolean;
  }[] {
    return responses.map((response) => {
      const { score, max_score, question_reference, item_reference } = response;

      const scorePercentage = Math.round((score / max_score) * 100);
      const attempts = response.response?.feedbackAttemptsCount || 0;
      const attemptScores = Shared.calculateAttemptScores([response])[0];

      return {
        questionReference: question_reference,
        itemReference: item_reference,
        score: scorePercentage,
        attempts,
        attemptScores,
        hintRequested: false,
      };
    });
  }
}

class ExerciseInfo {
  constructor(private report: IntermediateReportInterface) {}

  public get isFirstTime(): boolean {
    return this.result === null;
  }

  public get completion(): boolean {
    if (this.isFirstTime) return false;
    return this.result.status === 'completed';
  }

  public get score(): number | null {
    if (this.isFirstTime) return null;
    return this.result.score || null;
  }

  public get duration(): number {
    if (this.isFirstTime) return 0;
    return this.result.time / 1000; // should be in seconds, not milliseconds
  }

  public get attemptScores(): number[][] | null {
    if (this.isFirstTime) return null;
    return Shared.calculateAttemptScores(this.responses);
  }

  private get result() {
    return this.report.data.result;
  }

  private get responses() {
    return this.report.data.learnosityResponses.data[0]?.responses || [];
  }
}

@Injectable()
export class LearnosityConfig {
  private el: ElementRef = inject(ElementRef);
  private document: Document = inject(DOCUMENT);
  private renderer: Renderer2 = inject(Renderer2);
  private window: Window = inject(LEARNOSITY_WINDOW);
  private learnosityAssessmentApiService: LearnosityAssessmentApiService = inject(LearnosityAssessmentApiService);
  private permissionService: PermissionService = inject(PermissionService);

  private readonly learnosityContainerId = 'learnosity_assess';
  private _itemsApp: ItemsAppInterface;
  private event$ = new Subject<{ type: LearnosityEvent; payload: any }>();
  private config: LearnosityItemsConfigInterface;
  private manualSubmitListenStop: () => void;
  private savedItemReference: string | null = null;

  private listeningQuestions: {
    [questionId: string]: {
      question: QuestionObjectInterface;
      changedListener: () => void;
      validatedListener: () => void;
    };
  } = {};

  private revealedQuestions = new Set<string>();

  public get canSave(): boolean {
    // you cannot save in local_practice or review modes, only in submit_practice mode
    return this.isSubmitPracticeMode;
  }

  public get isReviewMode(): boolean {
    return this.config?.request.state === 'review';
  }

  public get isLocalPracticeMode(): boolean {
    return this.config?.request.type === 'local_practice';
  }

  public get isSubmitPracticeMode(): boolean {
    return this.config?.request.type === 'submit_practice';
  }

  public get currentItem() {
    return this.itemsApp?.getCurrentItem();
  }

  private get itemsApp(): ItemsAppInterface {
    return this._itemsApp;
  }

  private get learnosityContainer(): HTMLElement | null {
    return this.document.getElementById(this.learnosityContainerId);
  }

  private get questions(): QuestionObjectInterface[] {
    return Object.values(this.itemsApp.questions());
  }

  private get currentItemQuestions(): QuestionObjectInterface[] {
    const questionsByResponseId = this.itemsApp.questions();
    const responseIds = this.currentItem?.response_ids || [];
    return responseIds.map((responseId) => questionsByResponseId[responseId]);
  }

  private get hasManualSubmitQuestions(): boolean {
    return this.questions.some((question) => this.questionHasManualSubmit(question));
  }

  private get hasUseTtsQuestions(): boolean {
    return this.questions.some((question) => this.questionHasUseTts(question));
  }

  private get isLazyLoaded(): boolean {
    return this.config?.request.config.configuration.lazyload;
  }

  public setupLearnosityScript(
    config: LearnosityItemsConfigInterface
  ): Observable<{ type: LearnosityEvent; payload: any }> {
    this.config = config;

    this.isLearnosityScriptLoaded() ? this.initializeLearnosity() : this.createAndAppendLearnosityScript();

    return this.event$.asObservable();
  }

  public stopScript() {
    this.itemsApp.reset();
    this.document.removeEventListener('keydown', this.disablePreventingBackspace.bind(this));
    this.manualSubmitListenStop?.();

    this.event$.next({ type: LearnosityEvent.PLAYER_STOPPED, payload: {} });
    this.event$.complete();
  }

  public save(key?: string): void {
    if (key) this.savedItemReference = key;
    return this.itemsApp?.save();
  }

  public submit(): void {
    const submitOptions: SubmitOptionsInterface = {
      // we always skip the submit confirmation in the back-end config, so don't show it here either:
      show_submit_confirmation: false,
      show_submit_ui: true,
    };

    return this.itemsApp?.submit(submitOptions);
  }

  public getReportForLearningEvent(currentItemReference: string, { savedItemReferences = [] } = {}) {
    if (!this.config.registrationId) return;

    if (currentItemReference === 'final') {
      this.getFinalReport$()
        .pipe(first())
        .subscribe((report) => {
          this.event$.next({
            type: LearnosityEvent.REPORT_FINAL,
            payload: { currentItemReference, report, savedItemReferences },
          });
        });
    } else if (savedItemReferences.some((itemReference) => itemReference !== 'final')) {
      this.getIntermediateReport$()
        .pipe(first())
        .subscribe((report) => {
          if (savedItemReferences.includes('initial')) {
            this.event$.next({
              type: LearnosityEvent.REPORT_INITIAL,
              payload: { currentItemReference, report, savedItemReferences },
            });
          }

          if (savedItemReferences.some((itemReference) => itemReference !== 'initial' && itemReference !== 'final')) {
            this.event$.next({
              type: LearnosityEvent.REPORT_INTERMEDIATE,
              payload: { currentItemReference, report, savedItemReferences },
            });
          }
        });
    }
  }

  public getQuestionInfo(itemReference: string, report: IntermediateReportInterface) {
    return new QuestionInfo(itemReference, report);
  }

  public getExerciseInfo(report: IntermediateReportInterface) {
    return new ExerciseInfo(report);
  }

  private questionHasManualSubmit(question: QuestionObjectInterface): boolean {
    return !!question.getQuestion()?.custom_metadata?.has_manual_submit;
  }

  private questionHasUseTts(question: QuestionObjectInterface): boolean {
    const questionData = question.getQuestion();

    // Flashcard is hardcoded here because else we'd have to alter all existing flashcard questions
    // to include useTts in custom_metadata :\ Supporting both seems fine, allowing new exercises to specify use_tts
    const isFlashcard = questionData?.custom_type === 'flashcard_custom_question';
    const useTts = !!questionData?.custom_metadata?.use_tts;

    return isFlashcard || useTts;
  }

  private createLearnosityScript(): HTMLElement {
    const script = this.renderer.createElement('script');

    script.id = 'learnosity_items_script_id';
    script.type = 'text/javascript';
    script.src = `//items-ie.learnosity.com/?${learnosityScriptVersion}`;

    return script;
  }

  private isLearnosityScriptLoaded(): boolean {
    return !!this.document.getElementById('learnosity_items_script_id');
  }

  private createAndAppendLearnosityScript(): void {
    const script: HTMLElement = this.createLearnosityScript();

    script.onload = (): void => this.initializeLearnosity();

    this.renderer.appendChild(this.el.nativeElement.ownerDocument.head, script);
  }

  private initializeLearnosity() {
    if (!this.config) return;

    const itemsConfig = JSON.parse(JSON.stringify(this.config)); // workaround for readonly issue
    this._itemsApp = this.window.LearnosityItems.init(itemsConfig, `#${this.learnosityContainerId}`, {
      readyListener: () => {
        this.window.itemsApp = this._itemsApp;
        this.event$.next({ type: LearnosityEvent.PLAYER_READY, payload: this.itemsApp });
      },
      errorListener: (error: LearnosityErrorCodeInterface) => {
        this.event$.next({ type: LearnosityEvent.PLAYER_ERROR, payload: error });
        throw new Error(error.msg);
      },
    });

    this.listenForManualSubmit();

    this.listenToLearnosityEvents(this._itemsApp);

    if (!this.isReviewMode) {
      this.event$.pipe(on(LearnosityEvent.ITEM_LOADED)).subscribe((evt) => {
        this.listenQuestionChanges();
      });
      this.event$.pipe(on(LearnosityEvent.ITEM_UNLOAD)).subscribe((evt) => {
        this.unlistenQuestionChanges();
      });
    }

    this.document.addEventListener('keydown', this.disablePreventingBackspace.bind(this));
  }

  private listenToLearnosityEvents(itemsApp: ItemsAppInterface): void {
    const learnosityItemEventsOnce: [string, LearnosityEvent, ((payload?) => any)?][] = [
      [
        'item:load',
        LearnosityEvent.TEST_LOADED,
        () => ({
          currentItemReference: itemsApp.getCurrentItem().reference,
          hasManualSubmitQuestions: this.hasManualSubmitQuestions,
          hasUseTtsQuestions: this.hasUseTtsQuestions,
        }),
      ],
    ];

    learnosityItemEventsOnce.forEach(([event, replaceEvent, mapFn = (p) => p]) => {
      // console.log('*', event, payload);
      itemsApp.once(event, (payload) => this.event$.next({ type: replaceEvent, payload: mapFn(payload) }));
    });

    const learnosityItemEvents: [string, LearnosityEvent, ((payload?) => any)?][] = [
      ['test:start', LearnosityEvent.TEST_START],
      [
        'test:save:success',
        LearnosityEvent.TEST_SAVE_SUCCESS,
        (responseIds) => ({
          itemReference: this.savedItemReference,
          responseIds,
        }),
      ],
      [
        'test:save:error',
        LearnosityEvent.TEST_SAVE_ERROR,
        (error) => ({ error, itemReference: this.savedItemReference }),
      ],
      ['test:submit', LearnosityEvent.TEST_SUBMIT_START],
      [
        'test:finished:save',
        LearnosityEvent.TEST_FINISHED_SAVE,
        () => ({
          learnosityActivityId: itemsApp.getActivity()?.activity_id,
          learnositySessionId: itemsApp.getActivity()?.session_id,
          currentItemReference: itemsApp.getCurrentItem().reference,
        }),
      ],
      [
        'test:finished:submit',
        LearnosityEvent.TEST_FINISHED_SUBMIT,
        () => ({
          learnosityActivityId: itemsApp.getActivity()?.activity_id,
          learnositySessionId: itemsApp.getActivity()?.session_id,
          currentItemReference: itemsApp.getCurrentItem().reference,
        }),
      ],
      [
        'item:beforeunload',
        LearnosityEvent.ITEM_UNLOAD,
        (payload = {}) => ({
          ...payload,
          // very important to get the reference immediately during the item:beforeunload event,
          // the item reference will be a different one after the item is unloaded
          currentItemReference: itemsApp.getCurrentItem().reference,
        }),
      ],
      [
        'item:load',
        LearnosityEvent.ITEM_LOADED,
        (payload = {}) => ({
          ...payload,
          currentItemReference: itemsApp.getCurrentItem().reference,
          hasManualSubmitQuestions: this.hasManualSubmitQuestions,
          hasUseTtsQuestions: this.hasUseTtsQuestions,
        }),
      ],
      [
        'item:goto',
        LearnosityEvent.ITEM_GOTO,
        (currentItemIndex = null) => ({
          currentItemIndex,
          currentItemReference: itemsApp.getCurrentItem().reference,
        }),
      ],
    ];

    learnosityItemEvents.forEach(([event, replaceEvent, mapFn = (p) => p]) => {
      itemsApp.on(event, (payload) => this.event$.next({ type: replaceEvent, payload: mapFn(payload) }));
    });
  }

  private listenQuestionChanges() {
    this.currentItemQuestions.forEach((question) => {
      const lrnQuestion = question.getQuestion?.();
      if (!lrnQuestion) return;

      const questionId = lrnQuestion.metadata?.widget_reference;
      const responseId = lrnQuestion.response_id;
      const itemReference = lrnQuestion.metadata?.sheet_reference;
      if (this.listeningQuestions[questionId]) return; // already listening to this question

      const changedListener = () => {
        this.event$.next({ type: LearnosityEvent.ITEM_CHANGED, payload: { itemReference, questionId, responseId } });
      };
      const validatedListener = () => this.maybeRevealForQuestion(question);

      question.on?.('changed', changedListener);
      question.on?.('validated', validatedListener);

      this.listeningQuestions[questionId] = { question, changedListener, validatedListener };

      // Resume scenario: attempts may have been maxed in a prior session.
      this.maybeRevealForQuestion(question);
    });
  }

  private unlistenQuestionChanges() {
    Object.entries(this.listeningQuestions).forEach(
      ([questionId, { question, changedListener, validatedListener }]) => {
        question.off?.('changed', changedListener);
        question.off?.('validated', validatedListener);

        delete this.listeningQuestions[questionId];
      }
    );
  }

  private maybeRevealForQuestion(question: QuestionObjectInterface) {
    const widgetRef = question.getQuestion?.()?.metadata?.widget_reference;
    if (!widgetRef) return;
    if (this.revealedQuestions.has(widgetRef)) return;

    if (!this.questionSupportsValidate(question)) return;

    const maxAttempts = this.getMaxAttemptsForQuestion(question);
    if (!Number.isFinite(maxAttempts)) return;

    const attemptsCount = question.getResponse?.()?.feedbackAttemptsCount ?? 0;
    if (attemptsCount < maxAttempts) return;

    this.permissionService
      .hasPermission$(Permissions.feature.LEARNOSITY_CORRECT_ANSWER_FEEDBACK)
      .pipe(take(1))
      .subscribe((hasPermission) => {
        if (!hasPermission) return;
        if (this.revealedQuestions.has(widgetRef)) return;
        // Add to revealed Set BEFORE the validate call to prevent recursion
        // when validate() re-fires the 'validated' event.
        this.revealedQuestions.add(widgetRef);

        question.validate?.({ showCorrectAnswers: true });
        // Lock the question so the student cannot submit further attempts.
        question.disable?.();
      });
  }

  private getMaxAttemptsForQuestion(question: QuestionObjectInterface): number {
    const feedbackAttempts = question.getQuestion?.()?.feedback_attempts;
    if (feedbackAttempts === undefined || feedbackAttempts === null || feedbackAttempts <= 0) return Infinity;

    return feedbackAttempts;
  }

  private questionSupportsValidate(question: QuestionObjectInterface): boolean {
    if (typeof question.validate !== 'function') return false;

    const lrnQuestion = question.getQuestion?.();
    if (!lrnQuestion) return false;
    if (lrnQuestion.custom_type) return SUPPORTS_VALIDATE_CUSTOM_TYPES.has(lrnQuestion.custom_type);

    return !NON_VALIDATABLE_QUESTION_TYPES.has(lrnQuestion.type ?? '');
  }

  private getIntermediateReport$(): Observable<IntermediateReportInterface> {
    return this.learnosityAssessmentApiService.getIntermediateReport(this.config);
  }

  private getFinalReport$(): Observable<IntermediateReportInterface> {
    return this.getIntermediateReport$().pipe(
      repeatUntil(
        (report) => report.data?.learnosityResponses?.data?.[0]?.status !== LearnosityResponseStatusEnum.SCORING,
        [1, 1, 2, 3]
      )
    );
  }

  private listenForManualSubmit() {
    this.manualSubmitListenStop = this.renderer.listen(
      this.el.nativeElement,
      'manual-submit',
      this.manualSubmitHandler
    );
  }

  private manualSubmitHandler = (_: Event) => {
    // Learnosity will show an error if trying to submit in local_practice, so bail out here
    if (!this.canSave) {
      this.event$.next({
        type: LearnosityEvent.TEST_FINISHED_SUBMIT,
        payload: { manual: true },
      });

      return;
    }

    this.submit();
  };

  //todo: remove this once we figure out the issue with the backspace key in flashcards
  // also in author.component.ts
  private disablePreventingBackspace(e: KeyboardEvent) {
    if ((e.target as HTMLElement).tagName === 'DK-FLASHCARD' && e.key === 'Backspace') {
      e.stopImmediatePropagation();
    }
  }
}
